import { useEffect, useState } from "react";
import { Loader2, Lock, Zap } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n, d) => Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

function ChallengeChart({ challenge, revealed }) {
  const vis = challenge.visible.closes;
  const hid = revealed ? challenge.hidden.closes : [];
  const all = [...vis, ...hid];
  const total = vis.length + (challenge.hidden?.closes?.length || 24);
  const W = 720, H = 240;
  const lo = Math.min(...all), hi = Math.max(...all);
  const pad = (hi - lo) * 0.08 || 1;
  const y = (p) => 10 + (H - 20) * (1 - (p - (lo - pad)) / (hi + pad - (lo - pad)));
  const x = (i) => 8 + (W - 60) * (i / (total - 1));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" data-testid="challenge-chart">
      {vis.slice(1).map((c, i) => (
        <line key={i} x1={x(i)} y1={y(vis[i])} x2={x(i + 1)} y2={y(c)}
          stroke={c >= vis[i] ? "#7ee0a1" : "#ff7a7a"} strokeWidth="1.5" />
      ))}
      <line x1={x(vis.length - 1)} y1={8} x2={x(vis.length - 1)} y2={H - 8} stroke="#9ca3af" strokeWidth="1" strokeDasharray="4 4" opacity="0.6" />
      {!revealed && (
        <text x={x(vis.length - 1) + 12} y={26} fill="#e6c67a" fontSize="12">What happens next?</text>
      )}
      {revealed && hid.map((c, i) => {
        const prev = i === 0 ? vis[vis.length - 1] : hid[i - 1];
        return <line key={`h${i}`} x1={x(vis.length - 1 + i)} y1={y(prev)} x2={x(vis.length + i)} y2={y(c)}
          stroke="#e6c67a" strokeWidth="1.8" />;
      })}
      <text x={W - 4} y={y(vis[vis.length - 1]) + 4} fill="#d4d4d8" fontSize="11" textAnchor="end">{fmt(vis[vis.length - 1], challenge.decimals)}</text>
    </svg>
  );
}

export default function ChallengeTab() {
  const [ch, setCh] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ structure: "", liquidity: "", bias: "Long", entry: "", stop: "", target: "", reasoning: "" });

  const load = () => fetch(`${API}/practice/challenge/today`, { credentials: "include" })
    .then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d?.detail); setCh(d); })
    .catch((e) => setErr(e.message || "Challenge unavailable"));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (f.reasoning.trim().length < 10) return toast.error("Explain your reasoning first — that's the whole point.");
    setBusy(true);
    try {
      const r = await fetch(`${API}/practice/challenge/${ch.id}/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(f),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "Submission failed");
      setCh((c) => ({ ...c, submitted: true, submission: d.submission, hidden: d.hidden }));
      toast.success(`Challenge complete! +${d.submission.xp} XP`);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  if (err) return <div className="glass-card p-8 text-center text-sm text-zinc-500">{err}</div>;
  if (!ch) return <div className="py-16 text-center section-label text-[11px] text-zinc-600 animate-pulse">Loading today's challenge…</div>;

  const sub = ch.submission;
  const inp = "w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-[#9fd3ff]/40";
  return (
    <div className="space-y-4" data-testid="challenge-tab">
      <div className="glass-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="section-label text-[10px] text-[#e6c67a]">Today's Chart Challenge · {ch.day}</div>
            <h3 className="mt-1 font-heading text-2xl font-light">{ch.name}</h3>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[11px] text-[#e6c67a] border border-[#e6c67a]/30 rounded-full px-3 py-1.5">
            <Zap className="h-3.5 w-3.5" /> XP for process, not luck
          </span>
        </div>
        <p className="mt-2 text-[12px] text-zinc-500 font-light">
          The future candles are hidden. Read the structure, pick a bias, plan the trade, explain your reasoning — then lock it in and see what really happened.
        </p>
        <div className="mt-4"><ChallengeChart challenge={ch} revealed={ch.submitted} /></div>
      </div>

      {!ch.submitted ? (
        <div className="glass-card p-5 space-y-3" data-testid="challenge-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><div className="section-label text-[8px] text-zinc-500 mb-1">Structure read (HH/HL? BOS? CHoCH?)</div>
              <textarea rows={2} value={f.structure} onChange={(e) => setF({ ...f, structure: e.target.value })} className={`${inp} resize-none`} data-testid="challenge-structure" /></div>
            <div><div className="section-label text-[8px] text-zinc-500 mb-1">Liquidity read (what gets swept?)</div>
              <textarea rows={2} value={f.liquidity} onChange={(e) => setF({ ...f, liquidity: e.target.value })} className={`${inp} resize-none`} data-testid="challenge-liquidity" /></div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <div className="section-label text-[8px] text-zinc-500 mb-1.5">Bias</div>
              <div className="flex gap-2">
                {["Long", "Short", "Neutral"].map((b) => (
                  <button key={b} onClick={() => setF({ ...f, bias: b })}
                    className={`px-4 py-2 rounded-lg text-xs border transition-colors ${f.bias === b ? "border-[#9fd3ff] text-[#9fd3ff] bg-[#9fd3ff]/10" : "border-white/12 text-zinc-400"}`}
                    data-testid={`challenge-bias-${b.toLowerCase()}`}>{b}</button>
                ))}
              </div>
            </div>
            {["entry", "stop", "target"].map((k) => (
              <div key={k} className="w-28">
                <div className="section-label text-[8px] text-zinc-500 mb-1">{k}</div>
                <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inp} data-testid={`challenge-${k}`} />
              </div>
            ))}
          </div>
          <div>
            <div className="section-label text-[8px] text-zinc-500 mb-1">Your reasoning (required)</div>
            <textarea rows={3} value={f.reasoning} onChange={(e) => setF({ ...f, reasoning: e.target.value })} className={`${inp} resize-none`} data-testid="challenge-reasoning" />
          </div>
          <button onClick={submit} disabled={busy} className="btn-solid w-full py-3.5 text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="challenge-submit-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            {busy ? "Locking in & revealing…" : "Lock In My Analysis"}
          </button>
        </div>
      ) : sub && (
        <div className="glass-card p-5" data-testid="challenge-result">
          <div className="flex items-center justify-between">
            <span className="section-label text-[10px] text-[#9fd3ff]">The Reveal — MBM Review</span>
            <span className="inline-flex items-center gap-1.5 text-[#e6c67a] text-sm font-medium"><Zap className="h-4 w-4" /> +{sub.xp} XP</span>
          </div>
          <p className="mt-3 text-sm text-zinc-200 font-light leading-relaxed">{sub.what_happened}</p>
          <p className="mt-2 text-[13px] text-zinc-400 font-light italic">{sub.outcome_note}</p>
          <div className="mt-4 space-y-3">
            {sub.process_scores.map((s) => (
              <div key={s.key}>
                <div className="flex items-center justify-between text-[12px]">
                  <span className="text-zinc-200">{s.label}</span>
                  <span className={s.score >= 7 ? "text-[#7ee0a1]" : s.score >= 4 ? "text-[#e6c67a]" : "text-[#ff7a7a]"}>{s.score}/10</span>
                </div>
                <div className="mt-1 h-1 rounded-full bg-white/8"><div className="h-1 rounded-full bg-[#9fd3ff]" style={{ width: `${s.score * 10}%` }} /></div>
                <div className="mt-1 text-[11px] text-zinc-500 font-light">{s.note}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-2 text-[12px]">
            <div className="rounded-lg border border-[#7ee0a1]/20 bg-[#7ee0a1]/5 p-2.5 text-zinc-300"><span className="text-[#7ee0a1]">Well done: </span>{sub.did_well}</div>
            <div className="rounded-lg border border-[#e6c67a]/20 bg-[#e6c67a]/5 p-2.5 text-zinc-300"><span className="text-[#e6c67a]">Improve: </span>{sub.improve}</div>
          </div>
          <div className="mt-4 text-[11px] text-zinc-600">Come back tomorrow for a new chart.</div>
        </div>
      )}
    </div>
  );
}
